// API Service per comunicazione con il backend
class ApiService {
  constructor() {
    this.baseURL = 'http://api-qa.energytrades.ai:3000';
    this.token = null;
  }

  setToken(token) {
    this.token = token;
  }

  isAuthenticated() {
    return !!this.token;
  }

  getAuthHeaders() {
    return {
      'Content-Type': 'application/json',
      ...(this.token && { 'Authorization': `Bearer ${this.token}` })
    };
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      headers: this.getAuthHeaders(),
      ...options
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      // Handle empty responses
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      }

      return await response.text();
    } catch (error) {
      console.error(`API request failed: ${endpoint}`, error);
      throw error;
    }
  }

  // Authentication
  async login(username, password) {
    // Real login endpoint
    const response = await this.request('/api/auth/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ username, password })
    });

    if (response.success) {
      return response;
    } else {
      throw new Error(response.error || 'Login failed');
    }
  }

  async createMockToken(userId, tenantId) {
    // In produzione, questo sarebbe un vero endpoint di login
    // Per ora generiamo un mock token usando la logica del backend
    const payload = {
      sub: userId,
      tenantId: tenantId,
      roles: ['user', 'trader'],
      email: `${userId}@energytrades.ai`,
      iss: 'energytrades-ai',
      aud: 'energytrades-client',
      iat: Math.floor(Date.now() / 1000),
      exp: Math.floor(Date.now() / 1000) + (60 * 60) // 1 hour
    };

    // Simuliamo la creazione del JWT (in produzione sarebbe fatto dal server)
    const mockToken = btoa(JSON.stringify({
      header: { alg: 'HS256', typ: 'JWT' },
      payload: payload,
      signature: 'mock-signature'
    }));

    return `mock.${mockToken}.signature`;
  }

  async verifyToken() {
    return await this.request('/auth/token/verify', { method: 'POST' });
  }

  // Balancing API
  async createAvailability(availabilityData) {
    return await this.request('/balancing/availability', {
      method: 'POST',
      body: JSON.stringify(availabilityData)
    });
  }

  async acknowledgeActivation(ackData) {
    return await this.request('/balancing/activations/ack', {
      method: 'POST',
      body: JSON.stringify(ackData)
    });
  }

  // Market API
  async createOffer(segment, offerData) {
    return await this.request(`/api/offers`, {
      method: 'POST',
      body: JSON.stringify(offerData)
    });
  }

  async getAiSuggestion(offerData) {
    return await this.request('/api/ai/suggestion', {
      method: 'POST',
      body: JSON.stringify(offerData)
    });
  }

  async getPositions(page = 1) {
    return await this.request(`/api/positions?page=${page}`);
  }

  // Health check
  async getHealth() {
    return await this.request('/health');
  }

  async getWebSocketStatus() {
    return await this.request('/ws/status');
  }
}

export default ApiService;
